import type { ItemPhoto } from "@egalregal/shared";
import { useT, type Translate } from "../../shared/i18n";
import { useDemoMode } from "../demo/useDemoMode";
import { requests } from "../../shared/api";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteOutlineIcon,
  StarIcon,
} from "../../shared/components/icons";
import {
  PhotoCard,
  type PhotoCardAction,
  type PhotoCardRelated,
} from "../../shared/components/PhotoCard";

/**
 * The item photo's panel actions: move left/right, promote to cover and remove.
 * They always operate on the photo they are built for, so the panel can hand
 * each related sibling its own action set. The lightbox lives on the panel
 * image, not in this list.
 */
export function itemPhotoActions(
  photo: ItemPhoto,
  index: number,
  {
    t,
    count,
    orderedIds,
    onRemove,
    onReorder,
  }: {
    t: Translate;
    count: number;
    orderedIds: readonly string[];
    onRemove: (photoId: string) => void;
    onReorder: (ids: string[]) => void;
  },
): PhotoCardAction[] {
  const isFirst = index === 0;
  const isLast = index === count - 1;

  const move = (to: number): string[] => {
    const ids = [...orderedIds];
    const moved = ids[index];
    if (moved === undefined) return ids;
    ids.splice(index, 1);
    ids.splice(to, 0, moved);
    return ids;
  };

  return [
    ...(count > 1
      ? [
          {
            key: "left",
            label: t("itemPhotoMoveLeft"),
            icon: <ChevronLeftIcon fontSize="small" />,
            onClick: () => onReorder(move(index - 1)),
            disabled: isFirst,
          },
          {
            key: "cover",
            label: t("itemPhotoSetCover"),
            icon: <StarIcon fontSize="small" />,
            onClick: () =>
              onReorder([photo.id, ...orderedIds.filter((id) => id !== photo.id)]),
            disabled: isFirst,
          },
          {
            key: "right",
            label: t("itemPhotoMoveRight"),
            icon: <ChevronRightIcon fontSize="small" />,
            onClick: () => onReorder(move(index + 1)),
            disabled: isLast,
          },
        ]
      : []),
    {
      key: "remove",
      label: t("itemRemovePhoto"),
      icon: <DeleteOutlineIcon fontSize="small" />,
      onClick: () => onRemove(photo.id),
    },
  ];
}

/**
 * One item photo on the shared photo card (filter-table-reuse 04).
 *
 * The card shows fit media and the two-line caption; the info action opens the
 * sheet with the management actions: move the photo left/right, promote it to
 * cover, or remove it. Actions move out of the image so the photo is never
 * covered by controls.
 */
export function ItemPhotoTile({
  itemId,
  itemName,
  identity,
  related,
  photo,
  index,
  count,
  orderedIds,
  onOpen,
  onRemove,
  onReorder,
}: {
  itemId: string;
  itemName: string;
  /** Host container identity (never the item's UUID). */
  identity?: { uuid: string; name: string };
  related: readonly PhotoCardRelated[];
  photo: ItemPhoto;
  index: number;
  count: number;
  orderedIds: string[];
  onOpen: (index: number) => void;
  onRemove: (photoId: string) => void;
  onReorder: (ids: string[]) => void;
}) {
  const t = useT();
  const readOnly = useDemoMode();
  const kindLabel = photo.type === "nutrition" ? t("itemNutrition") : t("itemPhotos");

  return (
    <PhotoCard
      image={requests.media.itemPhotoPreviewUrl(itemId, photo.id)}
      imageAlt={itemName}
      // The item page already names the item in its h1; the photo card names
      // the kind of photo instead of repeating it on every tile.
      title={kindLabel}
      // The panel names the photo: item photos have no stored file name, so the
      // item name is the header and the kind stays the subtitle.
      panelTitle={itemName}
      panelSecondary={kindLabel}
      identity={identity}
      related={related}
      onOpen={() => onOpen(index)}
      actions={
        readOnly
          ? []
          : itemPhotoActions(photo, index, { t, count, orderedIds, onRemove, onReorder })
      }
    />
  );
}
